/* eslint-disable react/no-unstable-nested-components */
import { useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import {
    createSearchParams,
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import {
    Badge,
    Button,
    Card,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    MoreButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { useGetLabRequests } from "../../hooks/e-prescribe.queries";
import {
    useDownloadLabRequest,
    useSendLabRequest,
} from "../../hooks/e-prescribe.mutations";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import {
    ILabRequest,
    ISendLabRequestPayload,
    LabRequestStatuses,
} from "../../types/e-prescribe.types";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    handleRemoveEmptyParamFromQueryParams,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import Loader from "../../../../shared-ui/Loader/Loader";
import ListState from "../../../../components/ListState/ListState";
import { useDoesUserHaveRole } from "../../../../hooks/permissions";
import { UserRole } from "../../../user-management/types/user-management.types";
import { useDebounce } from "../../../../hooks/helpers";
import useToast from "../../../../hooks/useToast";
import SendLabRequestForm, {
    SEND_LAB_REQUEST_FORM_ID,
} from "../SendLabRequestForm/SendLabRequestForm";

export default function LabRequestList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [labRequestToSend, setLabRequestToSend] =
        useState<ILabRequest | null>(null);

    const navigate = useNavigate();
    const { toast } = useToast();

    const { clientId, noteId } = useParams();
    const location = useLocation();

    const isClientChart = location.pathname.includes("/clients");
    const isNoteSection = location.pathname.includes("/notes");

    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const statusFilter = searchParams.get("status") ?? "";
    const searchFilter = searchParams.get("search_string") ?? "";
    const createdDateFromFilter = searchParams.get("created_date_from");
    const createdDateToFilter = searchParams.get("created_date_to");
    const debouncedSearchFilter = useDebounce(searchFilter);

    const { data, isLoading, error, isSuccess } = useGetLabRequests({
        limit: limitFilter,
        page: pageFilter,
        client_id: clientId,
        note_id: noteId,
        status: statusFilter,
        search_string: debouncedSearchFilter,
        created_date_from: createdDateFromFilter,
        created_date_to: createdDateToFilter,
    });
    const downloadLabRequest = useDownloadLabRequest();
    const sendLabRequest = useSendLabRequest();
    const downloadIsMutating = useIsMutating(["download-lab-request"]);
    const sendIsMutating = useIsMutating(["send-lab-request"]);

    const canManageLabRequest = useDoesUserHaveRole([UserRole.PROVIDER]);
    const createLabRequestSearch = createSearchParams(
        handleRemoveEmptyParamFromQueryParams({
            client_id: clientId,
            note_id: noteId,
        })
    ).toString();

    const handleFilterChange = (paramName: string, value: string) => {
        if (value === "All") searchParams.delete(paramName);
        else {
            searchParams.set(paramName, value);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleInputFilterChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        handleFilterChange("search_string", event.target.value || "All");
    };

    const handleDateFilterChange = (paramName: string, date: Date | null) => {
        handleFilterChange(
            paramName,
            date ? format(date, "yyyy-MM-dd") : "All"
        );
    };

    const createdDateFrom = convertDateFilterStringToDate(
        createdDateFromFilter
    );
    const createdDateTo = convertDateFilterStringToDate(createdDateToFilter);

    const handleDownload = (labRequestId: string) => {
        downloadLabRequest.mutate(labRequestId, {
            onError: () => {
                toast({
                    mode: "error",
                    message: "Could not download the lab request",
                });
            },
        });
    };

    const handleSend = (payload: ISendLabRequestPayload) => {
        if (!labRequestToSend) return;

        sendLabRequest.mutate(
            { labRequestId: labRequestToSend.lab_request_id, payload },
            {
                onSuccess: (response) => {
                    setLabRequestToSend(null);
                    toast({
                        mode: "success",
                        message:
                            response.message ?? "Lab request sent successfully",
                    });
                },
                onError: (sendError) => {
                    toast({
                        mode: "error",
                        message:
                            sendError.response?.data.message ??
                            "Could not send the lab request",
                    });
                },
            }
        );
    };

    const columns: ColumnDef<ILabRequest>[] = [
        {
            accessorKey: "client",
            header: "Client",
            cell: ({ row }) => (
                <span className="capitalize">
                    {row.original?.client?.first_name &&
                    row.original?.client?.last_name
                        ? displayNameInRightFormat({
                              firstName: row.original.client.first_name,
                              lastName: row.original.client.last_name,
                          })
                        : "--"}
                </span>
            ),
        },
        {
            accessorKey: "created_by",
            header: "Created By",
            cell: ({ row }) => (
                <span className="capitalize">
                    {displayNameInRightFormat({
                        firstName: row.original.created_by.first_name,
                        lastName: row.original.created_by.last_name,
                    })}
                </span>
            ),
        },
        {
            accessorKey: "created_at",
            header: "Date Created",
            cell: ({ row }) => formatDate(row.original.created_at),
        },
        {
            accessorKey: "diagnosis_codes",
            header: "Diagnosis",
            cell: ({ row }) =>
                row.original?.diagnosis_codes?.length
                    ? row.original.diagnosis_codes.join(", ")
                    : "--",
        },
        {
            accessorKey: "comment",
            header: "Comment",
            cell: ({ row }) => (
                <span title={row.original?.comment ?? undefined}>
                    {row.original.comment
                        ? truncateString(row.original.comment, 40)
                        : "--"}
                </span>
            ),
        },
        {
            accessorKey: "tests",
            header: "Tests",
            cell: ({ row }) => {
                const tests = row.original?.tests
                    .map((test) => test.short_name ?? test.display_name)
                    .join(", ");

                return (
                    <span title={tests}>
                        {tests ? truncateString(tests, 50) : "--"}
                    </span>
                );
            },
        },
        {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => {
                const { status } = row.original;
                if (status === LabRequestStatuses.DRAFT) {
                    return (
                        <span>
                            <Badge
                                content={LabRequestStatuses.DRAFT}
                                type="neutral"
                            />
                        </span>
                    );
                }

                return (
                    <span>
                        <Badge
                            content={LabRequestStatuses.SIGNED}
                            type="success"
                        />
                    </span>
                );
            },
        },
        {
            accessorKey: "signed_by",
            header: "Signed By",
            cell: ({ row }) => {
                const { signature, signed_by: signedBy } = row.original;
                const signedByName =
                    signedBy?.first_name && signedBy.last_name
                        ? displayNameInRightFormat({
                              firstName: signedBy.first_name,
                              lastName: signedBy.last_name,
                          })
                        : "--";

                return (
                    <span className="capitalize">
                        {signedByName}
                        {signature ? ` (${signature})` : ""}
                    </span>
                );
            },
        },
        {
            accessorKey: "signed_at",
            header: "Date Signed",
            cell: ({ row }) =>
                row.original.signed_at
                    ? formatDate(row.original.signed_at)
                    : "--",
        },
        {
            id: "actions",
            header: "Actions",
            cell: ({ row }) => (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <MoreButton />
                    </DropdownTrigger>
                    <DropdownContent width="auto" align="end">
                        {canManageLabRequest &&
                        row.original.status === LabRequestStatuses.DRAFT ? (
                            <DropdownItem
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(
                                        `/lab-requests/${row.original.lab_request_id}/edit`
                                    );
                                }}
                            >
                                Edit
                            </DropdownItem>
                        ) : null}
                        {row.original.status === LabRequestStatuses.SIGNED ? (
                            <>
                                {canManageLabRequest && (
                                    <DropdownItem
                                        disabled={sendIsMutating > 0}
                                        onClick={() =>
                                            setLabRequestToSend(row.original)
                                        }
                                    >
                                        Send to client
                                    </DropdownItem>
                                )}
                                <DropdownItem
                                    disabled={downloadIsMutating > 0}
                                    onClick={() =>
                                        handleDownload(
                                            row.original.lab_request_id
                                        )
                                    }
                                >
                                    Download
                                </DropdownItem>
                            </>
                        ) : null}
                    </DropdownContent>
                </Dropdown>
            ),
        },
    ];

    return (
        <div>
            {labRequestToSend ? (
                <Dialog open>
                    <DialogContent
                        title="Send Lab Request"
                        variant="center"
                        saveText="Send"
                        showFooter
                        submitBtnFormValue={SEND_LAB_REQUEST_FORM_ID}
                        isSubmitBtnDisabled={sendIsMutating > 0}
                        isCancelBtnDisabled={sendIsMutating > 0}
                        handleCloseDialog={() => setLabRequestToSend(null)}
                    >
                        <SendLabRequestForm
                            defaultEmail={labRequestToSend?.client?.email}
                            onSubmit={handleSend}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <Card classNames="mb-[--spacing-header]">
                <div className="flex flex-col gap-y-[--spacing-items]">
                    {isNoteSection || isClientChart ? null : (
                        <SearchInput
                            placeholder="Search by client name or chart number"
                            containerClass="w-[400px] max-w-full"
                            onChange={handleInputFilterChange}
                            defaultValue={searchFilter}
                        />
                    )}
                    <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                        <div className="flex items-center gap-[--spacing-items] flex-wrap">
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {statusFilter || "Status"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="end" maxHeight={216}>
                                    <DropdownItem
                                        onClick={() =>
                                            handleFilterChange("status", "All")
                                        }
                                        isSelected={!statusFilter}
                                    >
                                        All
                                    </DropdownItem>
                                    {Object.values(LabRequestStatuses).map(
                                        (status) => (
                                            <DropdownItem
                                                key={status}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "status",
                                                        status
                                                    )
                                                }
                                                isSelected={
                                                    status === statusFilter
                                                }
                                            >
                                                {removeEnumUnderscore(status)}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date Created</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div className="mb-1">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={createdDateFrom}
                                            onChange={(date) =>
                                                handleDateFilterChange(
                                                    "created_date_from",
                                                    date
                                                )
                                            }
                                            maxDate={
                                                createdDateTo ?? new Date()
                                            }
                                            isClearable
                                        />
                                    </div>
                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={createdDateTo}
                                        onChange={(date) =>
                                            handleDateFilterChange(
                                                "created_date_to",
                                                date
                                            )
                                        }
                                        minDate={createdDateFrom}
                                        maxDate={new Date()}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="w-full min-[500px]:w-auto">
                            {canManageLabRequest ? (
                                <Button
                                    className="w-full cursor-pointer"
                                    onClick={() =>
                                        navigate({
                                            pathname: "/create-lab-request",
                                            search: createLabRequestSearch,
                                        })
                                    }
                                >
                                    Create Lab Request
                                </Button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </Card>
            {isLoading ? <Loader /> : null}
            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display lab request at this time please try again later`
                    }
                    context="general"
                />
            )}
            {isSuccess && data && data.data.length === 0 && (
                <ListState
                    stateHelperText="Lab request will appear here once added"
                    isError={false}
                    emptyMessage="No lab request added yet"
                    context="general"
                />
            )}
            {isSuccess && data && data.data.length > 0 && (
                <Table
                    columns={columns}
                    data={data.data}
                    isColumnVisible={{
                        client: !isClientChart && !isNoteSection,
                    }}
                />
            )}
        </div>
    );
}
